import React from 'react'

import { Trans, useTranslation } from 'react-i18next'

import { useParams } from 'react-router-dom'

import styled from 'styled-components'

import { Spinner, Text, baseColors } from '@fairhq/common'
import { usePracticeCriteria } from 'features/reportsEqualityAreas/hooks/usePracticeCriteria'
import { Layout } from 'layout/Layout'
import { PageTop } from 'layout/PageTop'

import { useGetAuditAreasQuery } from 'store/audit/auditApiWithQuery'
import { useAppSelector } from 'store/hooks'
import { State } from 'store/state'
import { QueryStatus } from 'store/types'

import { getLevelNumber } from 'utils/getLevelNumber'

import { LevelCard } from './LevelCard'

const LevelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

export const ReportsEqualityAreas = () => {
  const { sessionId } = useAppSelector((state: State) => ({
    sessionId: state.apiHeadersReducer.sessionId,
  }))

  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()

  const { queryStatus, practicesGroupedByLevel, firstIncompleteLevel } =
    usePracticeCriteria(code)

  const { data: areas } = useGetAuditAreasQuery({
    sessionId,
    query: { code },
  })

  // Area will be present, in the case it is not, try using code to get it from i18nexus
  const area = areas?.length ? areas[0] : { title: undefined, code }

  return (
    <Layout dark>
      <PageTop
        sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}
      >
        <Text type="heading" element="h1" size="xl" fontWeight={700}>
          {area.title ? area.title : <Trans i18nKey={`${code}.title`} />}
        </Text>
        <Text type="body" element="p" size="sm" fontWeight={400}>
          <Trans i18nKey={`${code}.description`} />
        </Text>
      </PageTop>
      {queryStatus === QueryStatus.LOADING && <Spinner center />}
      {queryStatus === QueryStatus.ERROR && (
        <Text
          element="p"
          type="body"
          size="md"
          fontWeight={600}
          color={baseColors.red}
        >
          {t('error.TryAgain')}
        </Text>
      )}
      {queryStatus === QueryStatus.SUCCESS &&
        practicesGroupedByLevel &&
        code && (
          <LevelsContainer>
            {Object.keys(practicesGroupedByLevel).map(levelKey => (
              <LevelCard
                key={levelKey}
                practices={practicesGroupedByLevel[levelKey]}
                levelNumber={getLevelNumber(levelKey)}
                isShowAllOpenInitialSate={firstIncompleteLevel === levelKey}
                areaCode={code}
              />
            ))}
          </LevelsContainer>
        )}
    </Layout>
  )
}
